import React, { useState, useRef, useEffect, useMemo } from "react";
import "./clientPicsDisplay.css";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import PanelActions from "../../AdminPages/SingleClientPage/PanelActions";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useMediaQuery } from "@mui/material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { QUERY_CLIENT_PHOTOS } from "../../../../utils/GraphQL/queries";
import { baImageUrl } from "../../../../utils/baImageUrl";
import { CircularProgress } from "@mui/material";
import copyImageToClip from "../../../../utils/copyImageToClip";
import MenuIcon from "@mui/icons-material/Menu";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import ImageModal from "../ImageModal/ImageModal";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import resizeImage from "../../../../utils/formatters/resizeImage";
import {
  NEW_CLIENT_PHOTO,
  DELETE_CLIENT_DOOR_PHOTO,
} from "../../../../utils/GraphQL/mutations";
// import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

/**
 * Where a photo's pixels actually come from — ONE definition, because there is
 * more than one caller.
 *
 * s3 URL for a server photo; a just-uploaded local temp carries its own base64
 * data-URI (instant preview, no round-trip); otherwise the /ba-image fallback.
 * Server photos no longer ship `imageData`.
 *
 * This was inline in the grid's map, and the Activity-focus effect had its own
 * copy that read `imgData` — a field no photo has. The grid looked right, the
 * Activity path opened an empty modal. Anything that needs a photo's src calls
 * this.
 */
const photoUrl = (item) =>
  item?.s3ImageUrl ?? item?.imageData ?? baImageUrl(item?._id);

/**
 * When a photo was added, in epoch ms.
 *
 * Derived from the ObjectId rather than from `dateEntered`, and that is not a
 * shortcut — it is exactly what `getClientActivity` does for `photo_uploaded`
 * (resolvers.js). The two MUST agree: the Activity feed and this grid are two
 * views of the same photos, and if they order them by different keys the row
 * at the top of Activity is not the first picture in the grid. That mismatch
 * is the bug this exists to prevent.
 *
 * `dateEntered` is the fallback for anything that has no real ObjectId.
 */
const photoTimeMs = (item) => {
  const oid = String(item?._id || "");
  if (/^[0-9a-fA-F]{24}$/.test(oid)) {
    const seconds = parseInt(oid.substring(0, 8), 16);
    if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
  }
  const entered = Number(item?.dateEntered);
  return Number.isFinite(entered) ? entered : 0;
};

/*
 * Two hosts, one component. `inPanel` renders this straight into the client
 * console's VS panel: no BdrDisclosure wrapper (the panel header already names
 * the section), open from the start so the lazy photo fetch runs immediately,
 * and none of the document-scroll behaviour — the console's page is
 * viewport-locked and the PANEL scrolls.
 *
 * Without it, this is the collapsible row the Edit branch still uses.
 */
const ClientPicsDisplay = ({
  heightTrigger,
  photoIdsArr,
  clientId,
  addPhotoIdToClient,
  removePhotoIdFromClient,
  focusRecord,
  onFocusHandled,
  inPanel,
}) => {
  const { handleSnack, handleClose: handleSnackClose } = useSnackbar();
  const [barCollapsed, setBarCollapsed] = useState(!inPanel);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");
  const [selectedImgNote, setSelectedImgNote] = useState("");
  const [selectedImgId, setSelectedImgId] = useState("");
  const [loading, setLoading] = useState(false);
  const [noPhotos, setNoPhotos] = useState(false);
  const [photosArr, setPhotosArr] = useState([]);
  /* Which photo's action menu is open: { anchor, id, imgId } or null. One
     piece of state for the whole grid - see the trigger's note. */
  const [photoMenu, setPhotoMenu] = useState(null);
  const [savePhoto] = useMutation(NEW_CLIENT_PHOTO);
  const [deletePhotoGQL] = useMutation(DELETE_CLIENT_DOOR_PHOTO);
  const [getImagesGQL] = useLazyQuery(QUERY_CLIENT_PHOTOS, {
    fetchPolicy: "network-only",
  });
  const [files, setFiles] = useState();
  const [uploadedImageTempUrl, setUploadedImageTempUrl] = useState([]);
  const smaller900 = useMediaQuery("(max-width:900px)");
  const clientPicsDiv = useRef();

  useEffect(() => {
    if (barCollapsed === true) {
      const id = setTimeout(() => {
        setLoading(false);
        setNoPhotos(false);
      }, 600);

      return () => clearTimeout(id);
    }

    if (barCollapsed === false) {
      if (photoIdsArr) {
        setLoading(true);
        setNoPhotos(false);
        getPhotos(photoIdsArr);
      }

      if (!photoIdsArr || photoIdsArr?.length === 0) {
        setLoading(false);
        setNoPhotos(true);
      }

      if (heightTrigger) {
        heightTrigger();
      }

      // The console's page does not scroll; its panel does.
      if (inPanel) return undefined;

      const id = setTimeout(() => {
        if (smaller900 === true && barCollapsed === true) {
          const navHeight = document.querySelector(
            "#admin-nav-height-marker"
          )?.offsetHeight;
          const pagePos = clientPicsDiv.current.offsetTop;

          const totalMobileScroll = pagePos - navHeight - 5;
          window.scrollTo(0, totalMobileScroll);
        }

        // if (smaller900 === false && barCollapsed === true) {
        //   clientPicsDiv.current.scrollIntoView({ block: "start" });
        // }
      }, 500);

      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barCollapsed, photoIdsArr]);

  useEffect(() => {
    createTemps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const openBarCollapse = () => {
    setBarCollapsed((prev) => !prev);
  };

  const modalHandler = (open, imgData, note, id) => {
    if (open === true) {
      setSelectedImg(imgData);
      setSelectedImgNote(note?.length > 0 ? note : "");
      setSelectedImgId(id?.length > 0 ? id : "");
    }

    setModalOpen(open);
  };

  const handleModalClose = () => {
    modalHandler(false);
  };

  const handleFileChange = (event) => {
    clearTempUrls();
    if (event.target.files.length === 0) {
    }

    if (event.target.files) {
      setFiles([...event.target.files]);

      return;
    }
  };

  const handleFileSave = async (event) => {
    clearTempUrls();
    if (files?.length === 0) {
      return;
    }

    for await (const file of files) {
      const date = Date.now();
      const resizedImg = await resizeImage(file);
      // Pre-fill the photo note with today's local-time date so the gallery
      // shows when the shot was taken. Formatting on the client (not using
      // the server's UTC `dateEntered`) avoids off-by-one at late-evening
      // uploads that would otherwise land in the next UTC day.
      const noteDate = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      if ((await resizedImg.length) > 0) {
        const photoData = await savePhoto({
          variables: {
            imageData: resizedImg,
            dateEntered: date.toString(),
            assignToClientId: clientId,
            note: noteDate,
          },
        });

        if (photoData.data) {
          console.log(photoData.data);
          // Mutation selection set is `storeImage { _id, ... }` — the id lives
          // under data.storeImage, not at data.* (was a silent bug that put
          // undefined into photosArr and broke delete + produced NaN keys).
          const newId = photoData.data?.storeImage?._id;
          if (newId) {
            addPhotoIdToClient(newId);
            setPhotosArr((prev) => [
              ...prev,
              { _id: newId, imageData: resizedImg, note: noteDate },
            ]);
          }
        }
      }

      return;
    }
  };

  const clearTempUrls = () => {
    setUploadedImageTempUrl([]);
  };

  const createTemps = async () => {
    const b64toBlob = (
      b64Data,
      contentType = "image/jpeg",
      sliceSize = 512
    ) => {
      const splitData = b64Data.split(",")[1];
      const byteCharacters = atob(splitData);
      const byteArrays = [];

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: contentType });
      return blob;
    };

    if (files) {
      for (const file of files) {
        const newSize = await resizeImage(file);
        const blob = b64toBlob(newSize);
        const url = URL.createObjectURL(blob);
        const name = file.name;

        setUploadedImageTempUrl((uploadedImageTempUrl) => [
          ...uploadedImageTempUrl,
          { url, name },
        ]);
      }

      //  handleUploadSubmit();

      return;
    }
  };

  const updateImgArr = (id, note) => {
    setPhotosArr((prevPhotos) =>
      prevPhotos.map((photo) => (photo._id === id ? { ...photo, note } : photo))
    );

    modalHandler(false);
  };

  const getPhotos = async (ids) => {
    if (photosArr?.length > 0) {
      setLoading(false);
      return;
    }

    const { data } = await getImagesGQL({
      variables: {
        pictureIdArr: [...ids],
      },
    });
    console.log(data);

    if (data?.getImageById?.length > 0) {
      setLoading(false);
      setPhotosArr([...data?.getImageById]);
      return;
    }

    setLoading(false);
    setNoPhotos(true);
    return;
  };

  const confirmDeletePhoto = (photoId) => {
    handleSnack(
      <div className="email-archive-snack">
        <span>Delete this photo?</span>
        <button
          className="email-page-action-img-button"
          onClick={(event) => {
            event.stopPropagation();
            handleSnackClose();
            deletePhoto(photoId);
          }}
        >
          <CheckIcon sx={{ fontSize: "20px", color: "#05F842" }} />
        </button>
        <button
          className="email-page-action-img-button"
          onClick={(event) => {
            event.stopPropagation();
            handleSnackClose();
          }}
        >
          <BlockIcon sx={{ fontSize: "20px", color: "#c80404" }} />
        </button>
      </div>,
      7000
    );
  };

  const deletePhoto = async (photoId) => {
    if (!photoId) return;

    setLoading(true);

    try {
      const { data } = await deletePhotoGQL({
        variables: { photoId, clientId },
        update: (cache) => {
          cache.evict({ id: `ClientDoorPhoto:${photoId}` });
          cache.gc();
        },
      });

      const result = data?.deleteClientDoorPhoto;

      if (result?.ok) {
        // remove from local image list
        setPhotosArr((prev) => prev.filter((p) => p._id !== photoId));
        handleSnack("Photo deleted", 1500);

        // remove from parent photoIds array
        if (removePhotoIdFromClient) removePhotoIdFromClient(photoId);

        modalHandler(false);
      } else {
        handleSnack(result?.reason || "Unable to delete photo", 2500);
      }
    } catch (e) {
      handleSnack(e.message || "Delete failed", 2500);
    } finally {
      setLoading(false);
    }
  };

  /*
   * An Activity row was clicked for a record this section owns. The Activity
   * panel cannot open these modals itself — they live in here, with the state
   * they depend on — so it switches to this section and hands over the id.
   * The nonce makes the same row clickable twice: a plain id would be an
   * unchanged value and the effect would not re-run after the modal is closed.
   */
  useEffect(() => {
    if (!focusRecord?.id) return;
    const match = photosArr?.find(
      (ph) => String(ph?._id || ph?.id) === String(focusRecord.id)
    );
    if (!match) return;
    /* photoUrl, NOT `match.imgData` — that field does not exist on a photo
       (it is `imageData`), so opening a photo from Activity handed the modal
       `undefined` and it rendered "Image Unavailable". The grid resolved the
       URL correctly right beside this, which is exactly how the two drifted. */
    modalHandler(
      true,
      photoUrl(match),
      match?.note,
      String(match?._id || match?.id)
    );
    /* Consumed. Only AFTER a match was found and acted on — the record has to
       survive until the lazy photo fetch lands, or arriving before the photos
       do would throw the instruction away before it could be carried out.
       Without this the record outlives the visit and re-opens this modal every
       time you come back to the tile. */
    if (onFocusHandled) onFocusHandled();
    // modalHandler is stable enough for this; photosArr arriving is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRecord, photosArr]);

  /*
   * NEWEST FIRST. `photosArr` arrives in server order, which left the most
   * recent photo at the END of the grid - so the "Photo added" row at the top
   * of the Activity feed pointed at the LAST picture here.
   *
   * Sorted on a COPY: photosArr is state, and the upload and delete paths both
   * still operate on it directly. Order is a render concern only.
   */
  const orderedPhotos = useMemo(
    () => [...(photosArr || [])].sort((a, b) => photoTimeMs(b) - photoTimeMs(a)),
    [photosArr]
  );

  const imageModal = (
      <ImageModal
        modalOpen={modalOpen}
        handleClose={handleModalClose}
        imgSrc={selectedImg}
        allowNoteUpdate={true}
        passedNote={selectedImgNote}
        imgId={selectedImgId}
        handleSnack={handleSnack}
        updateImgArr={updateImgArr}
      />
  );

  const listBody = (
    <>
      {/* In a panel these portal into the header's right-hand side; on the
          Edit branch they render here exactly as before. The picker stays a
          <label> wrapping the file input — that is what makes the whole
          control clickable without a hidden-input click shim — so it borrows
          the button classes rather than becoming a BdrButton. */}
      <PanelActions active={inPanel}>
        <div className="client-pics-upload-actions">
          <label className="bdr-btn bdr-btn--action bdr-btn--sm client-pics-upload-btn">
            <input
              onChange={handleFileChange}
              type="file"
              accept="image/*"
              name="clientImageUpload"
              multiple
            />
            {/* No `fontSize` and no literal space: the glyph is sized by the
                .bdr-btn icon rule in index.css so it matches every other
                action button, and .bdr-btn's own `gap` supplies the spacing —
                the {" "} here was stacking on top of it. */}
            <FileUploadIcon />
            {uploadedImageTempUrl?.length > 0
              ? "Change Photo(s)"
              : "Add Photo(s)"}
          </label>
          {uploadedImageTempUrl?.length > 0 && (
            <BdrButton size="sm" variant="confirm" onClick={handleFileSave}>
              Save To Client
            </BdrButton>
          )}
        </div>
      </PanelActions>
      <div className="client-pics-upload-box">
          {uploadedImageTempUrl?.length > 0 && (
            <div className="client-pics-uploaded-images-box">
              {uploadedImageTempUrl.map((file) => {
                return (
                  <img
                    className="client-pics-uploaded-img"
                    key={file.name + Date.now()}
                    src={file.url}
                    alt="Admin upload"
                    onClick={() => {
                      modalHandler(true, file.url);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
        {loading && (
          <div className="client-pics-loading-box">
            <CircularProgress color="inherit" size={20} />
          </div>
        )}
        {!loading && photosArr?.length > 0 && (
          <div className="client-pics-images-container">
            {orderedPhotos.map((item, index) => {
              const imgId = "imgEl" + item._id;
              const btnId = "btnEl" + item._id;
              const imgUrl = photoUrl(item);

              const html = (
                <div key={item._id + index} className="client-pics-img-box">
                  {/*
                   * ONE trigger, not one button per action. Two coloured pills
                   * sat on top of every photo and competed with the picture
                   * they were annotating; the list they open says what each
                   * action IS in words, which two bare glyphs never did.
                   *
                   * The menu itself is rendered ONCE for the whole grid (below
                   * the map) rather than per tile — twenty photos would mean
                   * twenty portalled menus, all but one of them closed.
                   */}
                  <button
                    className="client-pics-btn-sm client-pics-menu-btn"
                    id={btnId}
                    title="Photo actions"
                    aria-label="Photo actions"
                    aria-haspopup="menu"
                    aria-expanded={photoMenu?.id === item._id}
                    onClick={(event) =>
                      setPhotoMenu({
                        anchor: event.currentTarget,
                        id: item._id,
                        imgId,
                      })
                    }
                  >
                    <MenuIcon fontSize="inherit" />
                  </button>

                  <img
                    id={imgId}
                    className="client-pics-img bdr-border-std"
                    src={imgUrl}
                    alt={`door pic ${index}`}
                    onClick={() => {
                      modalHandler(
                        true,
                        imgUrl,
                        item?.note,
                        item?._id
                      );
                    }}
                  />
                  {item?.note?.length > 0 && (
                    <div className="client-pics-img-note-div">{item?.note}</div>
                  )}
                </div>
              );

              return html;
            })}
          </div>
        )}
      {!loading && noPhotos && (
        <div className="scc-list-empty">No photos to display</div>
      )}

      {/*
       * ONE menu for the whole grid, anchored to whichever trigger was last
       * clicked. Rendering it inside the map would portal a menu per photo.
       */}
      {photoMenu && (
        <BdrMenu
          anchorEl={photoMenu.anchor}
          ariaLabel="Photo actions"
          onClose={() => setPhotoMenu(null)}
          items={[
            {
              key: "copy",
              label: "Copy Photo",
              onClick: async () => {
                const img = document.querySelector(`#${photoMenu.imgId}`);
                if (!img) return;
                /* ClipboardItem takes the PROMISE, not the resolved blob -
                   that is the async-clipboard form Chrome requires, and it is
                   what keeps the write inside the click's user activation. */
                const blob = new ClipboardItem({
                  "image/png": copyImageToClip(img.src),
                });
                /* The toast follows the write: copyImageToClip REJECTS on a
                   load failure (it used to hang), which rejects the write. */
                navigator.clipboard
                  .write([blob])
                  .then(() => handleSnack("Image copied", 1500))
                  .catch((err) => {
                    console.error("[ClientPicsDisplay] copy photo failed", {
                      src: img.src,
                      err,
                    });
                    handleSnack("Couldn't copy the image", 2500);
                  });
              },
            },
            {
              key: "delete",
              label: "Delete Photo",
              danger: true,
              onClick: () => {
                const id = photoMenu.id;
                if (typeof id === "string" && id.length > 0) {
                  confirmDeletePhoto(id);
                  return;
                }
                handleSnack(
                  "Photo not yet saved — reopen the panel to refresh",
                  2500
                );
              },
            },
          ]}
        />
      )}
    </>
  );

  if (inPanel) {
    return (
      <div className="scc-list" ref={clientPicsDiv}>
        {imageModal}
        {listBody}
      </div>
    );
  }

  return (
    <div
      id="client-pics-height-marker"
      className="client-pics-container"
      ref={clientPicsDiv}
    >
      {imageModal}
      <BdrDisclosure
        open={!barCollapsed}
        onToggle={openBarCollapse}
        label="Client Photos"
        icon={<PhotoLibraryIcon />}
        /* The IDs arrive with the client; only the images themselves are
           fetched on expand. So the count is known before opening, where
           `photosArr` would read 0 until the lazy query lands. */
        count={photoIdsArr?.length}
        emptyText="No photos yet"
      >
        {listBody}
      </BdrDisclosure>
    </div>
  );
};

export default ClientPicsDisplay;
